import { useGlobalContainerRef } from '@/wc/helper/ref';
import {
  FormSelect,
  IconHelp,
  SelectProps,
  Tooltip,
} from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { useCallback, useMemo } from 'react';
import { Control, FieldPath, FieldValues } from 'react-hook-form';
import styles from './styles.module.scss';

const cx = classnames.bind(styles);

type PaymentStatusOption = { label: string; value: string; tooltip?: string };

type PaymentStatusSelectProps<TFieldValues extends FieldValues> = {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  options: PaymentStatusOption[];
  placeholder?: string;
};

export function PaymentStatusSelect<TFieldValues extends FieldValues>({
  control,
  name,
  options,
  placeholder,
}: PaymentStatusSelectProps<TFieldValues>) {
  const container = useGlobalContainerRef();

  const renderOption = useCallback(
    (option: PaymentStatusOption) => {
      if (!option.tooltip) {
        return <span>{option.label}</span>;
      }
      return (
        <div className={cx('option-container')}>
          <span>{option.label}</span>
          <Tooltip
            title={option.tooltip}
            placement='right'
            getPopupContainer={container}
          >
            <div
              className={cx('icon-container')}
              onClick={(e) => e.stopPropagation()}
            >
              <IconHelp size={14} />
            </div>
          </Tooltip>
        </div>
      );
    },
    [container]
  );

  const renderedOptions = useMemo(
    () =>
      options.map((option) => ({
        ...option,
        render: renderOption(option),
      })),
    [options, renderOption]
  );

  const selectProps: SelectProps = useMemo(
    () => ({
      options: renderedOptions,
      isAllowClear: true,
      size: 'small',
      placeholder: placeholder ?? '',
    }),
    [renderedOptions, placeholder]
  );

  return (
    <FormSelect<TFieldValues>
      control={control}
      name={name}
      inputId={name}
      testId={name}
      selectProps={selectProps}
    />
  );
}

PaymentStatusSelect.displayName = 'PaymentStatusSelect';
